import React, { useEffect, useRef } from 'react';

interface Props {
  event: {
    name: string;
    date: string;
  };
  settings: {
    titleText: string;
    textColor: string;
    font: string;
    backgroundColor: string;
    accentColor?: string;
    secondaryColor?: string;
    logo?: string;
    customFont?: {
      url: string;
      family: string;
    };
    background?: {
      url: string;
      opacity: number;
    };
  };
}

const PREVIEW_SAMPLE = {
  name: 'John Runner',
  rank: 42,
  category_position: 12,
  chip_time: '3:45:22'
};

const SYSTEM_FONTS = {
  'System Default': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif',
  'System UI': 'system-ui, sans-serif',
  'SF Pro': '-apple-system, BlinkMacSystemFont, sans-serif',
  'Segoe UI': '"Segoe UI", sans-serif',
  'Inter': 'Inter, system-ui, sans-serif'
};

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
  });
};

export default function CertificatePreview({ event, settings }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    updatePreview();
  }, [event, settings]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const updatePreview = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d')!;
    const scale = 0.4; // Scale down for preview
    canvas.width = 1200 * scale;
    canvas.height = 800 * scale;
    ctx.scale(scale, scale);

    try {
      setError(null);
      
      // Background color
      ctx.fillStyle = settings.backgroundColor;
      ctx.fillRect(0, 0, 1200, 800);

      // Background image
      if (settings.background?.url) {
        try {
          const bgImage = await loadImage(settings.background.url);
          const bgScale = Math.max(
            1200 / bgImage.width,
            800 / bgImage.height
          );
          const width = bgImage.width * bgScale;
          const height = bgImage.height * bgScale;
          const x = (1200 - width) / 2;
          const y = (800 - height) / 2;

          ctx.globalAlpha = settings.background.opacity;
          ctx.drawImage(bgImage, x, y, width, height);
          ctx.globalAlpha = 1;
        } catch (err) {
          console.warn('Failed to load background image:', err);
        }
      }

      // Logo
      let logoHeight = 0;
      if (settings.logo) {
        try {
          const logo = await loadImage(settings.logo);
          const logoWidth = 240;
          logoHeight = (logo.height / logo.width) * logoWidth;
          ctx.drawImage(logo, (1200 - logoWidth) / 2, 60, logoWidth, logoHeight);
        } catch (err) {
          console.warn('Failed to load logo:', err);
        }
      }

      const getFontFamily = () => {
        if (settings.font === 'Custom Font' && settings.customFont) {
          return settings.customFont.family;
        }
        return SYSTEM_FONTS[settings.font] || SYSTEM_FONTS['System Default'];
      };

      // Title
      ctx.fillStyle = settings.textColor;
      const titleY = logoHeight ? 160 + logoHeight : 200;

      ctx.font = `bold 52px ${getFontFamily()}`;
      ctx.textAlign = 'center';
      ctx.fillStyle = `${settings.textColor}10`;
      ctx.fillText(settings.titleText, 1200 / 2 + 2, titleY + 2);
      ctx.fillStyle = settings.textColor;
      ctx.fillText(settings.titleText, 1200 / 2, titleY);

      // Event name
      ctx.font = `500 40px ${getFontFamily()}`;
      const eventNameY = titleY + 80;
      ctx.fillText(event.name, 1200 / 2, eventNameY);

      // Date
      ctx.font = `400 28px ${getFontFamily()}`;
      const dateY = eventNameY + 50;
      ctx.fillText(formatDate(event.date), 1200 / 2, dateY);

      // "Presented to" text
      ctx.font = `italic 28px ${getFontFamily()}`;
      ctx.fillText('Presented to', 1200 / 2, dateY + 80);

      // Participant name
      ctx.font = `600 48px ${getFontFamily()}`;
      const nameY = dateY + 140;
      ctx.fillStyle = `${settings.textColor}10`;
      ctx.fillText(PREVIEW_SAMPLE.name, 1200 / 2 + 2, nameY + 2);
      ctx.fillStyle = settings.textColor;
      ctx.fillText(PREVIEW_SAMPLE.name, 1200 / 2, nameY);

      // Achievement details
      const detailsY = nameY + 100;
      const detailsSpacing = 240;
      const detailHeight = 80;
      const detailWidth = 200;

      [-1, 0, 1].forEach((position, index) => {
        const x = 1200 / 2 + position * detailsSpacing - detailWidth / 2;
        const y = detailsY;
        
        // Card background with gradient
        const gradient = ctx.createLinearGradient(x, y, x, y + detailHeight);
        gradient.addColorStop(0, `${settings.accentColor}08`);
        gradient.addColorStop(1, `${settings.accentColor}15`);
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.roundRect(x, y, detailWidth, detailHeight, 12);
        ctx.fill();

        // Card content
        ctx.fillStyle = settings.textColor;
        ctx.font = `400 24px ${getFontFamily()}`;
        ctx.textAlign = 'center';
        
        const details = [
          { label: 'Official Time', value: PREVIEW_SAMPLE.chip_time },
          { label: 'Overall Position', value: `#${PREVIEW_SAMPLE.rank}` },
          { label: 'Category Position', value: `#${PREVIEW_SAMPLE.category_position}` }
        ][index];
        
        ctx.fillText(details.label, x + detailWidth / 2, y + 35);
        ctx.font = `600 24px ${getFontFamily()}`;
        ctx.fillText(details.value, x + detailWidth / 2, y + 65);
      });
    } catch (err: any) {
      setError(err.message);
      console.error('Preview error:', err);
    }
  };

  return (
    <div className="relative bg-white rounded-lg shadow-inner p-2 flex items-center justify-center">
      <canvas
        ref={canvasRef}
        className="max-w-full h-auto rounded border border-gray-200"
      />
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50 bg-opacity-90 rounded">
          <p className="text-red-600 text-sm p-4 text-center">
            Error generating preview: {error}
          </p>
        </div>
      )}
    </div>
  );
}